"use client";

import { Subtitle } from "@tremor/react";
import { LinkWithIcon } from "components/LinkWithIcon";
import { Mapping, Rules, Workflows, ExportIcon } from "components/icons";
import { Session } from "next-auth";
import { Disclosure } from "@headlessui/react";
import { IoChevronUp } from "react-icons/io5";
import { TbTopologyRing } from "react-icons/tb";
import { FaVolumeMute } from "react-icons/fa";
import { IoMdGitMerge } from "react-icons/io";
import { useTopology } from "@/app/(keep)/topology/model/useTopology";
import clsx from "clsx";
import { AILink } from "./AILink";
import { useConfig } from "@/utils/hooks/useConfig";
import { useTenantConfiguration } from "@/utils/hooks/useTenantConfiguration";
import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
type NoiseReductionLinksProps = {
  session: Session | null;
};
type TogglableLinkProps = {
  disabledConfigKey: string;
  children: ReactNode;
};
const TogglableLink = ({
  children,
  disabledConfigKey
}: TogglableLinkProps) => {
  const {
    data: tenantConfig,
    isLoading
  } = useTenantConfiguration();
  const {
    data: envConfig
  } = useConfig();
  if (isLoading || !tenantConfig) {
    return <div className="flex gap-2 items-center h-7 pl-3">
        <Skeleton className="min-h-5 min-w-5" />
        <Skeleton className="min-h-5 min-w-24" containerClassName="min-h-5 min-w-24" />
      </div>;
  }
  if (!tenantConfig?.[disabledConfigKey] && !(envConfig as any)?.[disabledConfigKey]) {
    return <>{children}</>;
  }
};
export const NoiseReductionLinks = ({
  session
}: NoiseReductionLinksProps) => {
  const isNOCRole = session?.userRole === "noc";
  const {
    topologyData
  } = useTopology();
  const {
    data: tenantConfig,
    isLoading
  } = useTenantConfiguration();
  const noiseReductionKeys = {
    HIDE_NAVBAR_DEDUPLICATION: "HIDE_NAVBAR_DEDUPLICATION",
    HIDE_NAVBAR_CORRELATION: "HIDE_NAVBAR_CORRELATION",
    HIDE_NAVBAR_WORKFLOWS: "HIDE_NAVBAR_WORKFLOWS",
    HIDE_NAVBAR_SERVICE_TOPOLOGY: "HIDE_NAVBAR_SERVICE_TOPOLOGY",
    HIDE_NAVBAR_MAPPING: "HIDE_NAVBAR_MAPPING",
    HIDE_NAVBAR_EXTRACTION: "HIDE_NAVBAR_EXTRACTION",
    HIDE_NAVBAR_MAINTENANCE_WINDOW: "HIDE_NAVBAR_MAINTENANCE_WINDOW",
    HIDE_NAVBAR_AI_PLUGINS: "HIDE_NAVBAR_AI_PLUGINS"
  };
  if (isNOCRole) {
    return null;
  }
  if (!Object.values(noiseReductionKeys).some(key => !tenantConfig?.[key])) {
    return null;
  }
  return <Disclosure as="div" className="space-y-0.5" defaultOpen data-sentry-element="Disclosure" data-sentry-component="NoiseReductionLinks" data-sentry-source-file="NoiseReductionLinks.tsx">
      <Disclosure.Button className="w-full flex justify-between items-center px-2" data-sentry-element="unknown" data-sentry-source-file="NoiseReductionLinks.tsx">
        {({
        open
      }) => <>
            {tenantConfig && <>
                <Subtitle className="text-xs ml-2 text-gray-900 font-medium uppercase">
                  NOISE REDUCTION
                </Subtitle>
                <IoChevronUp className={clsx({
            "rotate-180": open
          }, "mr-2 text-slate-400")} />
              </>}
            {!tenantConfig && <div className="flex items-center h-7 pl-2">
                <Skeleton className="min-h-5 min-w-36" />
              </div>}
          </>}
      </Disclosure.Button>

      <Disclosure.Panel as="ul" className="space-y-0.5 p-1 pr-1" data-sentry-element="unknown" data-sentry-source-file="NoiseReductionLinks.tsx">
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_DEDUPLICATION} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/deduplication" icon={IoMdGitMerge} testId="deduplication" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Deduplication</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_CORRELATION} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/rules" icon={Rules} testId="rules" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Correlations</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_WORKFLOWS} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/workflows" icon={Workflows} testId="workflows" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Workflows</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>

        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_SERVICE_TOPOLOGY} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/topology" icon={TbTopologyRing} isBeta={!topologyData || topologyData.length === 0} count={topologyData?.length === 0 ? undefined : topologyData?.length} testId="service-topology" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Service Topology</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_MAPPING} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/mapping" icon={Mapping} testId="mapping" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Mapping</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_EXTRACTION} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/extraction" icon={ExportIcon} testId="extraction" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Extraction</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_MAINTENANCE_WINDOW} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <LinkWithIcon href="/maintenance" icon={FaVolumeMute} testId="maintenance" data-sentry-element="LinkWithIcon" data-sentry-source-file="NoiseReductionLinks.tsx">
              <Subtitle className="text-xs" data-sentry-element="Subtitle" data-sentry-source-file="NoiseReductionLinks.tsx">Maintenance Windows</Subtitle>
            </LinkWithIcon>
          </li>
        </TogglableLink>
        <TogglableLink disabledConfigKey={noiseReductionKeys.HIDE_NAVBAR_AI_PLUGINS} data-sentry-element="TogglableLink" data-sentry-source-file="NoiseReductionLinks.tsx">
          <li>
            <AILink data-sentry-element="AILink" data-sentry-source-file="NoiseReductionLinks.tsx"></AILink>
          </li>
        </TogglableLink>
      </Disclosure.Panel>
    </Disclosure>;
};